/**
 * A regra de quais abas um item mostra e o que um clique faz com elas.
 *
 * ## Por que isto é um módulo separado, sem React
 *
 * Estava dentro do `AreaPicker`, e **lá não havia como testar**. A suíte do
 * projeto roda em Node: ela lê os arquivos do front e varre o JSX, mas não
 * monta componente. Uma regra que decide o que vai para o banco não pode viver
 * onde nenhum teste alcança — e a prova é o defeito abaixo, que passou por
 * revisão, portão e deploy.
 *
 * ## O defeito que isto conserta (26/10)
 *
 * O seletor montava as fichas a partir das abas **ativas**, e devolvia a
 * escolha filtrada pela mesma lista:
 *
 * ```js
 * onChange([...AREAS, TODAS].filter((a) => proximas.has(a)));
 * ```
 *
 * Com isso, um item ligado a uma aba **desligada** perdia esse vínculo no
 * primeiro clique em qualquer outra ficha — a aba não aparecia na lista, então
 * não sobrevivia ao filtro. E `replaceAreas` apaga e regrava o que o cliente
 * mandou, então a perda chegava ao banco.
 *
 * O que torna isto grave é a contradição: a tela de abas promete, com essas
 * palavras, que **desligar preserva os vínculos** e que religar traz os itens
 * de volta "exatamente como estavam". A promessa valia até alguém editar o
 * item — e aí os vínculos iam embora sem mensagem, sem log, sem nada.
 *
 * ## A regra, agora explícita
 *
 * As fichas são: o coringa, as abas ativas, e **as abas que este item já tem**
 * mesmo que desligadas. A última parte é a correção: o que o item carrega
 * aparece, para poder ser visto, mantido ou retirado por escolha — nunca por
 * omissão.
 */

/** O coringa. Não é uma aba: faz o item aparecer em todas. */
export const TODAS = 'Todos';

/**
 * As abas que o seletor desenha, na ordem.
 *
 * Primeiro as ativas, na ordem que o servidor mandou; depois as desligadas que
 * este item carrega, que entram no fim porque são exceção e não escolha
 * disponível.
 */
export function abasVisiveis(ativas, escolhidas = []) {
  const vivas = ativas.filter((a) => a !== TODAS);
  const presas = escolhidas.filter((a) => a !== TODAS && !vivas.includes(a));
  return [...vivas, ...[...new Set(presas)]];
}

/** Se esta aba está no item e já não está mais ligada no cadastro. */
export function estaDesligada(aba, ativas) {
  return aba !== TODAS && !ativas.includes(aba);
}

/**
 * O que um clique numa ficha produz.
 *
 * `Todos` é coringa e limpa o resto: marcar "Todos + RH" diz duas coisas em que
 * a segunda não muda nada, e deixar as duas acesas daria a impressão de que
 * tirar "Todos" preservaria "RH" — não preservaria, porque quem marcou "Todos"
 * nunca escolheu RH de fato.
 *
 * A ordem de saída é a das abas, não a dos cliques, para o valor gravado não
 * depender de em que sequência alguém mexeu.
 */
export function alternarAba(ativas, escolhidas = [], aba) {
  const tinhaTodas = escolhidas.includes(TODAS);

  if (aba === TODAS) {
    // Desligar o coringa deixa vazio, e não uma seleção que ninguém fez.
    return tinhaTodas ? [] : [TODAS];
  }

  const proximas = new Set(escolhidas.filter((a) => a !== TODAS));
  if (proximas.has(aba)) proximas.delete(aba);
  else proximas.add(aba);

  // ⚠ A ordem de referência inclui as desligadas que o item carrega. Era aqui
  // que elas se perdiam: filtrar só pelas ativas as deixava fora do resultado.
  const ordem = abasVisiveis(ativas, [...proximas]);
  return ordem.filter((a) => proximas.has(a));
}

export default { TODAS, abasVisiveis, estaDesligada, alternarAba };
